import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { cleanOptions } from "@/lib/format";
import { useSheetHistory } from "@/hooks/useSheetHistory";

// Modal for doneness / style / milk / variant options.
//
// The group headings are ours and translate; the choices inside them are the
// dish document's own words ("Medium Rare", "Oat +10K"), which the kitchen and
// the order ticket share — translating those would send the kitchen a value it
// has never seen.
const OptionModal = ({ dish, onClose, onConfirm }) => {
  useSheetHistory(true, onClose);
  const { t } = useTranslation();
  const [doneness, setDoneness] = useState(null);
  const [style, setStyle] = useState(null);
  const [milk, setMilk] = useState(null);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    if (dish?.doneness) setDoneness(dish.doneness[2] || dish.doneness[0]);
    if (dish?.style) setStyle(dish.style[0]);
    if (dish?.milk) setMilk(dish.milk[0]); // default = "Regular"
    if (dish?.variant) setVariant(dish.variant[0]);
  }, [dish]);

  if (!dish) return null;
  const needsDoneness = !!dish.doneness;
  const needsStyle = !!dish.style;
  const needsMilk = !!dish.milk;
  const needsVariant = !!dish.variant;
  const canConfirm =
    (!needsDoneness || !!doneness) &&
    (!needsStyle || !!style) &&
    (!needsMilk || !!milk) &&
    (!needsVariant || !!variant);

  return (
    <div
      data-testid="option-modal"
      className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-[#F9F9F7] rounded-t-[28px] sm:rounded-3xl p-6 pb-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="option-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center"
          aria-label={t("common.close")}
        >
          <X size={18} />
        </button>
        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">
          {t("options.overline")}
        </div>
        <h3 className="text-2xl font-black tracking-tight text-neutral-900 mb-5 pr-10">{dish.name}</h3>

        {needsDoneness && (
          <ChipGroup
            label={t("options.doneness")}
            options={dish.doneness}
            value={doneness}
            onChange={setDoneness}
            testIdPrefix="opt-doneness"
          />
        )}

        {needsStyle && (
          <ChipGroup
            label={t("options.style")}
            options={dish.style}
            value={style}
            onChange={setStyle}
            testIdPrefix="opt-style"
          />
        )}

        {needsMilk && (
          <ChipGroup
            label={t("options.milk")}
            hint={t("options.milkHint")}
            options={dish.milk}
            value={milk}
            onChange={setMilk}
            testIdPrefix="opt-milk"
          />
        )}

        {needsVariant && (
          <ChipGroup
            label={t("options.flavor")}
            options={dish.variant}
            value={variant}
            onChange={setVariant}
            testIdPrefix="opt-variant"
          />
        )}

        {/* Unset options are dropped: the API rejects a null where a choice is expected. */}
        <button
          data-testid="option-modal-confirm"
          disabled={!canConfirm}
          onClick={() => onConfirm(cleanOptions({ doneness, style, milk, variant }))}
          className="w-full h-14 mt-2 rounded-2xl bg-[#E34A27] text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform disabled:opacity-40"
        >
          {t("options.addToOrder")}
        </button>
      </div>
    </div>
  );
};

const ChipGroup = ({ label, hint, options, value, onChange, testIdPrefix }) => (
  <div className="mb-5">
    <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">{label}</div>
    {hint && <div className="text-[11px] text-neutral-500 mb-2 -mt-1">{hint}</div>}
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`${testIdPrefix}-${opt.replace(/\s|\+|\//g, "-").toLowerCase()}`}
          onClick={() => onChange(opt)}
          className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
            value === opt
              ? "bg-neutral-900 text-white border-neutral-900"
              : "bg-white text-neutral-700 border-neutral-200"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default OptionModal;
